import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, Eye, EyeOff, MailCheck } from 'lucide-react';
import { auth, affiliate } from '../lib/api.js';
import { supabase, isNative } from '../lib/supabase.js';
import { nativeSocialSignIn } from '../lib/native.js';
import { useSession } from '../lib/session.jsx';
import { Button, Field } from '../components/ui.jsx';
import Aurora from '../components/Aurora.jsx';
import Crystal from '../components/Crystal.jsx';
import { Mark } from '../components/Layout.jsx';

const MIN_PASSPHRASE = 8;

export default function SignIn() {
  const { notice: sessionNotice } = useSession();
  const [params] = useSearchParams();
  const native = isNative();
  const [mode, setMode] = useState(params.get('mode') === 'signup' ? 'signup' : 'signin');
  const [form, setForm] = useState({ email: '', password: '', confirm: '', fullName: '', invite: params.get('ref') ?? affiliate.pending() ?? '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  // 'form' | 'confirm' — the latter after a sign-up that needs an emailed link.
  const [stage, setStage] = useState('form');

  const signup = mode === 'signup';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const switchMode = () => { setMode(signup ? 'signin' : 'signup'); setError(null); setNotice(null); };

  const validate = () => {
    if (signup && form.fullName.trim().length < 2) return 'Tell the server what to call you.';
    if (form.password.length < MIN_PASSPHRASE) return `Your passphrase needs at least ${MIN_PASSPHRASE} characters.`;
    if (signup && form.password !== form.confirm) return 'The two passphrases do not match.';
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const problem = validate();
    if (problem) { setError(problem); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      if (!signup) {
        await auth.signInWithPassword(form.email.trim(), form.password);
        return; // the session provider takes over from here
      }
      if (form.invite.trim()) affiliate.remember(form.invite);   // claimed on the first session
      const { session } = await auth.signUp(form.email.trim(), form.password, form.fullName.trim());
      if (!session) setStage('confirm');
      // With a session the provider re-renders straight into the app.
    } catch (err) { setError(friendly(err)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true); setError(null);
    try { await auth.resendConfirmation(form.email.trim()); setNotice('Sent again. Give it a minute.'); }
    catch (err) { setError(friendly(err)); }
    finally { setBusy(false); }
  };

  const social = async (provider) => {
    setBusy(true); setError(null);
    try {
      if (native) await nativeSocialSignIn(provider, supabase);
      else {
        const { error: e } = await supabase.auth.signInWithOAuth({
          provider, options: { redirectTo: window.location.origin },
        });
        if (e) throw e;
      }
    } catch (err) { setError(friendly(err)); setBusy(false); }
  };

  return (
    <div className="relative min-h-screen grid lg:grid-cols-[1fr_1fr]">
      <Aurora />

      {/* left: the crystal, waiting (web, wide screens only) */}
      <div className="relative z-[1] hidden lg:flex flex-col justify-between p-10">
        <Link to="/" className="flex items-center gap-2 text-sm font-medium w-fit" style={{ color: 'var(--text-2)' }}>
          <ArrowLeft size={15} /> Back
        </Link>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.4, ease: [0.2, 0.7, 0.2, 1] }}
                    className="flex flex-col items-center">
          <Crystal xp={signup ? 30 : 900} hue={signup ? 20 : 286} seed={signup ? 'seed-new' : 'seed-return'} size={400} />
          <p className="display-i text-2xl -mt-6" style={{ color: 'var(--text-2)' }}>
            {signup ? 'A seed, waiting to be woken.' : 'It remembers you.'}
          </p>
        </motion.div>
        <p className="text-xs" style={{ color: 'var(--text-3)' }}>Tokens are granted by the server, never by your device.</p>
      </div>

      {/* right: the form */}
      <div className="relative z-[1] flex items-center justify-center px-6 py-12"
           style={native ? { paddingTop: 'calc(env(safe-area-inset-top) + 2rem)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' } : undefined}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                    className="glass w-full max-w-[26rem] p-8 md:p-10">
          {/* In the installed app this IS the front door, so the mark is not a link home. */}
          <div className="lg:hidden mb-8">{native ? <Mark compact /> : <Link to="/"><Mark compact /></Link>}</div>

          {stage === 'confirm' ? (
            <ConfirmStage email={form.email} busy={busy} error={error} notice={notice}
                          onResend={resend} onBack={() => { setStage('form'); setMode('signin'); setNotice(null); setError(null); }} />
          ) : (
            <>
              <p className="eyebrow">{signup ? 'Begin' : 'Welcome back'}</p>
              <h1 className="display text-[2.4rem] mt-2">
                {signup ? <>Wake your <span className="display-i lit">crystal</span>.</> : <>Open the <span className="display-i lit">line</span>.</>}
              </h1>

              <form onSubmit={submit} className="mt-8 space-y-4" noValidate>
                {signup && (
                  <Field label="Name" required autoComplete="name" placeholder="What should the server call you?"
                         value={form.fullName} onChange={set('fullName')} />
                )}
                <Field label="Email" type="email" required autoComplete="email" inputMode="email" autoCapitalize="none"
                       placeholder="you@somewhere.com" value={form.email} onChange={set('email')} />
                <PassphraseField label="Passphrase" value={form.password} onChange={set('password')}
                                 autoComplete={signup ? 'new-password' : 'current-password'}
                                 placeholder={`At least ${MIN_PASSPHRASE} characters`}
                                 hint={signup ? <Strength value={form.password} /> : null} />
                {signup && (
                  <PassphraseField label="Confirm passphrase" value={form.confirm} onChange={set('confirm')}
                                   autoComplete="new-password" placeholder="Once more, to be sure"
                                   hint={form.confirm && form.confirm !== form.password ? 'Does not match yet.' : null} />
                )}

                {sessionNotice && !error && <Msg tone="sage">{sessionNotice}</Msg>}
                {error && <Msg tone="rust">{error}</Msg>}
                {notice && <Msg tone="sage">{notice}</Msg>}

                <Button type="submit" disabled={busy} className="w-full mt-2" size="lg">
                  {busy && <Loader2 size={15} className="animate-spin" />}
                  {signup ? 'Wake it' : 'Open the line'}
                </Button>
                {signup && (
                  <Field label="Invite code" placeholder="Optional — from a creator you follow" autoCapitalize="characters"
                         autoComplete="off" value={form.invite} onChange={set('invite')}
                         hint={form.invite ? 'Your welcome bonus arrives once your crystal wakes.' : null} />
                )}
                {signup && (
                  <p className="text-xs leading-relaxed text-center" style={{ color: 'var(--text-3)' }}>
                    By waking a crystal you agree to keep your passphrase to yourself.
                  </p>
                )}
              </form>

              <div className="flex items-center gap-4 my-6">
                <span className="h-px flex-1" style={{ background: 'var(--edge)' }} />
                <span className="eyebrow">or</span>
                <span className="h-px flex-1" style={{ background: 'var(--edge)' }} />
              </div>

              <div className="space-y-2.5">
                <Button variant="glass" className="w-full" disabled={busy} onClick={() => social('google')}>
                  <GoogleMark /> Continue with Google
                </Button>
                {/* Apple mandates Sign in with Apple wherever another social provider is
                    offered. Colours are hardcoded per Apple's brand guidelines. */}
                <Button variant="glass" className="w-full" disabled={busy} onClick={() => social('apple')}
                        style={{ background: '#000', color: '#fff', border: '1px solid #000' }}>
                  <AppleMark /> Continue with Apple
                </Button>
              </div>

              <button onClick={switchMode}
                      className="w-full text-center mt-7 text-sm font-medium hover:opacity-100 opacity-70 transition-opacity">
                {signup ? 'Already have a crystal? Sign in' : 'No crystal yet? Wake one'}
              </button>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function ConfirmStage({ email, busy, error, notice, onResend, onBack }) {
  return (
    <div>
      <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-6"
           style={{ background: 'color-mix(in oklab, var(--sage) 14%, transparent)', color: 'var(--sage)' }}>
        <MailCheck size={22} />
      </div>
      <p className="eyebrow">One more step</p>
      <h1 className="display text-[2.2rem] mt-2">Check your <span className="display-i lit">inbox</span>.</h1>
      <p className="mt-4 text-sm leading-relaxed" style={{ color: 'var(--text-2)' }}>
        We sent a link to <strong style={{ color: 'var(--text)' }}>{email}</strong>. Open it to wake your crystal —
        the link works on this device or any other.
      </p>
      <div className="mt-6 space-y-3">
        {error && <Msg tone="rust">{error}</Msg>}
        {notice && <Msg tone="sage">{notice}</Msg>}
        <Button variant="glass" className="w-full" disabled={busy} onClick={onResend}>
          {busy && <Loader2 size={15} className="animate-spin" />} Send it again
        </Button>
        <button onClick={onBack} className="w-full text-center text-sm font-medium opacity-70 hover:opacity-100 transition-opacity">
          Back to sign in
        </button>
      </div>
    </div>
  );
}

function PassphraseField({ label, hint, ...rest }) {
  const [shown, setShown] = useState(false);
  return (
    <label className="block space-y-2">
      <span className="eyebrow block">{label}</span>
      <span className="relative block">
        <input className="input pr-11" type={shown ? 'text' : 'password'} required minLength={MIN_PASSPHRASE}
               autoCapitalize="none" spellCheck={false} {...rest} />
        <button type="button" onClick={() => setShown((s) => !s)} tabIndex={-1}
                aria-label={shown ? 'Hide passphrase' : 'Show passphrase'}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: 'var(--text-2)' }}>
          {shown ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </span>
      {hint && <span className="block text-xs" style={{ color: 'var(--text-3)' }}>{hint}</span>}
    </label>
  );
}

/** Three-step strength hint: length, then mixed character classes. */
function Strength({ value }) {
  if (!value) return `At least ${MIN_PASSPHRASE} characters. A few words you will remember work best.`;
  const classes = [/[a-z]/, /[A-Z]/, /\d/, /[^\w]/].filter((r) => r.test(value)).length;
  const score = value.length < MIN_PASSPHRASE ? 0 : value.length >= 14 || classes >= 3 ? 2 : 1;
  const label = ['Too short', 'Good enough', 'Strong'][score];
  const tone = ['var(--rust)', 'var(--gold)', 'var(--sage)'][score];
  return (
    <span className="flex items-center gap-2">
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="h-1 w-6 rounded-full" style={{ background: i <= score && (score > 0 || i === 0) ? tone : 'var(--edge)' }} />
        ))}
      </span>
      <span style={{ color: tone }}>{label}</span>
    </span>
  );
}

/** Supabase error messages are for developers; these are for people. */
function friendly(err) {
  const m = err?.message ?? String(err);
  if (/invalid login credentials/i.test(m)) return 'That email and passphrase do not match.';
  if (/already registered|already exists/i.test(m)) return 'A crystal already answers to that email. Sign in instead.';
  if (/rate limit|too many/i.test(m)) return 'Too many tries. Take a breath and try again in a minute.';
  if (/email not confirmed/i.test(m)) return 'Open the confirmation link we emailed you first.';
  if (/cancel/i.test(m)) return null; // user backed out of the native sheet — not an error
  return m;
}

function Msg({ tone, children }) {
  return (
    <p className="text-sm leading-relaxed px-3.5 py-2.5 rounded-xl"
       style={{ color: `var(--${tone})`, background: `color-mix(in oklab, var(--${tone}) 12%, transparent)` }}>
      {children}
    </p>
  );
}
const GoogleMark = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);
const AppleMark = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="#fff" aria-hidden>
    <path d="M16.37 12.7c-.03-2.8 2.29-4.14 2.39-4.21-1.3-1.9-3.33-2.17-4.05-2.2-1.72-.18-3.36 1.01-4.24 1.01-.87 0-2.22-.99-3.65-.96-1.88.03-3.61 1.09-4.58 2.77-1.95 3.39-.5 8.41 1.4 11.16.93 1.35 2.04 2.86 3.49 2.81 1.4-.06 1.93-.91 3.62-.91s2.17.91 3.65.88c1.51-.03 2.46-1.37 3.38-2.72 1.07-1.56 1.51-3.07 1.53-3.15-.03-.01-2.94-1.13-2.94-4.48zM13.6 4.47c.77-.94 1.29-2.24 1.15-3.54-1.11.05-2.46.74-3.26 1.68-.71.83-1.34 2.16-1.17 3.43 1.24.1 2.51-.63 3.28-1.57z" />
  </svg>
);
